import { computed, nextTick, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type { TreeOption } from 'naive-ui'
import { Category } from '@/material/web/category'
import {
  getChildCategories,
  getCollection,
  getGoodsByCategory,
  getGoodsInfoByIds,
  getGoodsResources,
  getMaterialPreviewUrl,
  getTopCategories,
  searchGoods,
  toggleCollection,
  type MaterialAuth,
  type MaterialCollectionItem,
  type MaterialGoods,
  type MaterialGoodsQueryResult,
  type MaterialResource,
  type MaterialSearchParams,
  type MaterialSubCategory,
  type MaterialTopCategory,
} from '@/material/web/api'
import { runWithMaterialSessionRetry } from '@/features/material-library/session'
import { registerMaterialPortraitCategoryId } from '@/features/material-library/targets'
import { getResolvableFlatAssetThumbs } from '@/material/web/previewUtils'

export type MaterialSearchType = '1' | '2'

export interface MaterialTreeNode extends TreeOption {
  key: string
  label: string
  nodeType: 'l2' | 'leaf'
  topId: string
  categoryId: string
  goodsCount: number
  filterType?: 0 | 2
  loaded?: boolean
  children?: MaterialTreeNode[]
}

type FavoriteTreeNode = MaterialTreeNode & {
  favoriteGoodsIds?: number[]
}

interface AppliedSearch {
  text: string
  type: MaterialSearchType
}

type MaterialCatalogMessageApi = {
  error: (content: string) => void
  success: (content: string) => void
}

/** 判断当前登录态是否具备访问素材库接口所需的 uid / session。 */
function isValidAuth(auth: MaterialAuth | null): auth is MaterialAuth {
  return !!auth && auth.uid > 0 && !!auth.sessionId
}

function registerKnownPortraitCategories(items: MaterialTopCategory[]): void {
  for (const item of items) {
    const name = String(item.name || '').trim()
    if (name === '狗橙' || name === '狗橙和朋友们' || name === '狗橙和朋友') {
      registerMaterialPortraitCategoryId(item.id)
    }
  }
}

function filterVisibleTopCategories(items: MaterialTopCategory[]): MaterialTopCategory[] {
  return items.filter((item) => String(item.id) !== Category.特效)
}

type CategoryMetaRegistrar = (categoryId: string, topId: string, identifyName: string) => void

let categoryMetaRegistrar: CategoryMetaRegistrar = () => {}

/** 构造二级分类节点。 */
function createL2Node(topId: string, item: MaterialSubCategory): MaterialTreeNode {
  const identifyName = String(item.identify_name || '').trim()
  categoryMetaRegistrar(item.id, topId, identifyName)
  const goodsCount = Number(item.goods_count || 0)
  return {
    key: `l2:${topId}:${item.id}`,
    label: `${item.name}（${item.goods_count}）`,
    nodeType: 'l2',
    topId,
    categoryId: item.id,
    goodsCount,
    loaded: false,
    isLeaf: false,
    children: [
      createLeafNode(topId, item.id, 0, '全部', goodsCount, undefined, identifyName),
    ],
  }
}

/** 构造最终可选中的叶子分类节点。 */
function createLeafNode(
  topId: string,
  categoryId: string,
  filterType: 0 | 2,
  label: string,
  goodsCount: number,
  favoriteGoodsIds?: number[],
  identifyName = '',
): FavoriteTreeNode {
  categoryMetaRegistrar(categoryId, topId, identifyName)
  return {
    key: `mat:${topId}:${categoryId}:${filterType}`,
    label,
    nodeType: 'leaf',
    topId,
    categoryId,
    goodsCount,
    filterType,
    favoriteGoodsIds,
    isLeaf: true,
  }
}

/** 在分类树中按 key 查找节点。 */
function findNodeByKey(nodes: MaterialTreeNode[], key: string): MaterialTreeNode | null {
  for (const node of nodes) {
    if (node.key === key) return node
    if (node.children?.length) {
      const found = findNodeByKey(node.children, key)
      if (found) return found
    }
  }
  return null
}

/** 找到某个分类节点下第一个可直接加载商品列表的叶子节点。 */
function findFirstLeaf(node: MaterialTreeNode): MaterialTreeNode | null {
  if (node.nodeType === 'leaf') return node
  for (const child of node.children ?? []) {
    const found = findFirstLeaf(child)
    if (found) return found
  }
  return null
}

/** 将树节点 key 还原成查询条件。 */
function parseLeafKey(key: string): { topId: string; filterId: string; filterType: 0 | 2 } | null {
  if (!key.startsWith('mat:')) return null
  const parts = key.split(':')
  if (parts.length !== 4) return null
  const filterType = parts[3] === '2' ? 2 : parts[3] === '0' ? 0 : null
  if (filterType === null) return null
  return {
    topId: parts[1] ?? '',
    filterId: parts[2] ?? '',
    filterType,
  }
}

/** 提取收藏列表中的素材 ID，便于前端快速判断收藏状态。 */
function extractCollectionIds(collection: MaterialCollectionItem[]): number[] {
  return collection.map((item) => Number(item.goods_id || 0)).filter((id) => id > 0)
}

/** 收藏 ID 列表稳定 key，用于判断缓存是否过期。 */
function buildFavoriteGoodsIdsKey(goodsIds: number[]): string {
  return goodsIds.join(',')
}

/**
 * 素材库目录组合逻辑：
 * 负责分类树、商品分页、搜索、收藏和预览资源加载，
 * 供素材库弹窗与预览面板共享状态。
 */
export function useMaterialLibraryCatalog(
  authRef: ComputedRef<MaterialAuth | null>,
  message: MaterialCatalogMessageApi,
) {
  const DEFAULT_PAGE_SIZE = 15

  const loadingInit = ref(false)
  const loadingList = ref(false)
  const previewLoading = ref(false)

  const topCategories = ref<MaterialTopCategory[]>([])
  const treeData = ref<MaterialTreeNode[]>([])
  const expandedTreeKeys = ref<Array<string | number>>([])
  const selectedTreeKeys = ref<Array<string | number>>([])

  const goodsList = ref<MaterialGoods[]>([])
  const currentGoods = ref<MaterialGoods | null>(null)
  const currentResources = ref<MaterialResource[]>([])

  const collectionGoodsIds = ref<number[]>([])
  const collectionItems = ref<MaterialCollectionItem[]>([])
  const favoritesOnly = ref(false)

  const selectedTopId = ref('')
  const selectedFilterId = ref('')
  const selectedFilterType = ref<0 | 2>(0)

  const page = ref(1)
  const pageSize = ref(DEFAULT_PAGE_SIZE)
  const total = ref(0)

  const searchText = ref('')
  const searchType = ref<MaterialSearchType>('1')
  const appliedSearch = ref<AppliedSearch | null>(null)

  let listRequestId = 0
  let previewRequestId = 0
  let topCategoryRequestId = 0
  let treeSelectRequestId = 0
  const nodeLoadPromises = new Map<string, Promise<void>>()
  const favoriteGoodsCache = new Map<number, MaterialGoods>()
  const favoriteGoodsByTopId = new Map<string, MaterialGoods[]>()
  const favoriteGoodsIdsKeyByTop = new Map<string, string>()
  const categoryMetaMap = ref(new Map<string, { topId: string; identifyName: string }>())

  /** 记录分类节点元信息，供推荐页按商品 cid 反查真实顶部分类。 */
  function registerCategoryMeta(categoryId: string, topId: string, identifyName: string) {
    const cid = String(categoryId || '').trim()
    if (!cid) return
    categoryMetaMap.value.set(cid, {
      topId: String(topId || '').trim(),
      identifyName: String(identifyName || '').trim(),
    })
  }

  categoryMetaRegistrar = registerCategoryMeta

  /** 按 identify_name 匹配顶部分类 ID。 */
  function resolveTopIdByIdentifyName(identifyName: string): string {
    const name = String(identifyName || '').trim()
    if (!name) return ''
    return topCategories.value.find((item) => item.identify_name === name)?.id ?? ''
  }

  /**
   * 解析商品真实顶部分类：推荐页混合素材需按商品自身分类决定卡片与预览形态。
   */
  function resolveGoodsTopCategoryId(goods: MaterialGoods | null | undefined): string {
    if (!goods) return selectedTopId.value
    const currentTop = selectedTopId.value
    if (currentTop !== Category.推荐) return currentTop

    const sourceTop = String(goods.sourceTopCategoryId || '').trim()
    if (sourceTop && topCategories.value.some((item) => item.id === sourceTop)) {
      return sourceTop
    }

    const cid = String(goods.cid || '').trim()
    if (cid && topCategories.value.some((item) => item.id === cid)) {
      return cid
    }

    const meta = cid ? categoryMetaMap.value.get(cid) : undefined
    if (meta?.identifyName) {
      const mapped = resolveTopIdByIdentifyName(meta.identifyName)
      if (mapped) return mapped
    }
    if (meta?.topId && meta.topId !== Category.推荐) {
      return meta.topId
    }

    console.warn('[material-library] 无法解析推荐商品真实分类', { goodsId: goods.id, cid })
    return currentTop
  }

  const currentTopCategory = computed(
    () => topCategories.value.find((item) => item.id === selectedTopId.value) ?? null,
  )
  const isRecommendTopCategory = computed(() => selectedTopId.value === Category.推荐)
  const topCategoryMode = computed<'vertical' | 'horizontal' | 'music'>(() => {
    const topId = selectedTopId.value
    if ([Category.音乐, Category.音效].includes(topId as Category)) return 'music'
    if ([Category.背景, Category.CG, Category.UI, Category.特效].includes(topId as Category)) {
      return 'horizontal'
    }
    return 'vertical'
  })
  const currentGoodsTopCategoryId = computed(() => resolveGoodsTopCategoryId(currentGoods.value))
  const currentCollected = computed(() => {
    const id = Number(currentGoods.value?.id || 0)
    return id > 0 && collectionGoodsIds.value.includes(id)
  })
  const hasSearch = computed(() => !!appliedSearch.value)

  function withCatalogSession<T>(context: string, task: (auth: MaterialAuth) => Promise<T>) {
    return runWithMaterialSessionRetry(authRef, context, task)
  }

  function resetCatalogResults() {
    goodsList.value = []
    currentGoods.value = null
    currentResources.value = []
    total.value = 0
    previewLoading.value = false
  }

  /** 切换分类/翻页/素材时立即清空右侧预览，避免旧图残留。 */
  function clearPreviewState() {
    previewRequestId += 1
    currentGoods.value = null
    currentResources.value = []
    previewLoading.value = false
  }

  /** 收藏侧栏：仅按顶部分类聚合，顺序与顶栏一致。 */
  function createFavoriteTree(): MaterialTreeNode[] {
    const topIdSet = new Set(topCategories.value.map((item) => String(item.id)))
    const buckets = new Map<string, number[]>()
    for (const item of collectionItems.value) {
      const topId = String(item.goods_cate_id || '').trim()
      const goodsId = Number(item.goods_id || 0)
      if (!topId || goodsId <= 0 || !topIdSet.has(topId)) continue
      buckets.set(topId, [...(buckets.get(topId) ?? []), goodsId])
    }
    return topCategories.value
      .map((top) => {
        const topId = String(top.id)
        const ids = buckets.get(topId)
        if (!ids?.length) return null
        const resolvedCount = favoriteGoodsByTopId.get(topId)?.length ?? ids.length
        return createLeafNode(topId, topId, 0, `${top.name}（${resolvedCount}）`, resolvedCount, ids)
      })
      .filter((node): node is FavoriteTreeNode => node !== null)
  }

  function invalidateFavoriteGoodsCache(topId?: string) {
    if (topId) {
      favoriteGoodsByTopId.delete(topId)
      favoriteGoodsIdsKeyByTop.delete(topId)
      return
    }
    favoriteGoodsByTopId.clear()
    favoriteGoodsIdsKeyByTop.clear()
  }

  function resetSearchState(clearInput: boolean) {
    appliedSearch.value = null
    if (clearInput) searchText.value = ''
  }

  function expandTreeNode(key: string | number) {
    expandedTreeKeys.value = [...new Set([...expandedTreeKeys.value, key])]
  }

  async function refreshCollection() {
    const currentAuth = authRef.value
    if (!isValidAuth(currentAuth)) return
    try {
      const collection = await withCatalogSession('refreshCollection', (auth) => getCollection(auth))
      collectionItems.value = collection
      collectionGoodsIds.value = extractCollectionIds(collection)
      invalidateFavoriteGoodsCache()
      if (favoritesOnly.value) {
        treeData.value = createFavoriteTree()
      }
    } catch {
      collectionItems.value = []
      collectionGoodsIds.value = []
    }
  }

  async function loadPreview(goods: MaterialGoods | null) {
    const requestId = ++previewRequestId
    if (!isValidAuth(authRef.value) || !goods) {
      currentResources.value = []
      previewLoading.value = false
      return
    }

    currentResources.value = []
    previewLoading.value = true
    try {
      const resources = await withCatalogSession('loadPreview', (auth) => getGoodsResources(auth, goods.id))
      if (requestId !== previewRequestId) return
      currentResources.value = resources
    } catch (error) {
      if (requestId !== previewRequestId) return
      currentResources.value = []
      message.error(error instanceof Error ? error.message : '加载素材预览失败')
    } finally {
      if (requestId === previewRequestId) {
        previewLoading.value = false
      }
    }
  }

  /**
   * 按顶部分类批量回填收藏商品（对齐工具 3.0 MaterialCollection.getCollection）。
   */
  async function loadFavoriteGoodsForTop(topId: string, goodsIds: number[]): Promise<MaterialGoods[]> {
    if (!goodsIds.length || !isValidAuth(authRef.value)) return []

    const idsKey = buildFavoriteGoodsIdsKey(goodsIds)
    const cachedKey = favoriteGoodsIdsKeyByTop.get(topId)
    const cachedList = favoriteGoodsByTopId.get(topId)
    if (cachedList && cachedKey === idsKey) {
      return cachedList
    }

    const fetched = await withCatalogSession('getGoodsInfoByIds', (auth) => getGoodsInfoByIds(auth, goodsIds))
    const goodsMap = new Map<number, MaterialGoods>()
    for (const goods of fetched) {
      const goodsId = Number(goods.id || 0)
      if (goodsId > 0) {
        goodsMap.set(goodsId, goods)
      }
    }

    const ordered: MaterialGoods[] = []
    const missing: number[] = []
    for (const goodsId of goodsIds) {
      const goods = goodsMap.get(goodsId)
      if (goods) {
        ordered.push(goods)
        favoriteGoodsCache.set(goodsId, goods)
      } else {
        missing.push(goodsId)
      }
    }

    if (missing.length) {
      console.warn('[material-library] 收藏商品批量回填缺失', { topId, missing })
    }

    favoriteGoodsByTopId.set(topId, ordered)
    favoriteGoodsIdsKeyByTop.set(topId, idsKey)
    return ordered
  }

  /** 商品列表缩略图缺失时，用资源预览图补齐。 */
  async function ensureGoodsThumb(goods: MaterialGoods): Promise<MaterialGoods> {
    if (goods.thumbUrl) return goods
    try {
      const resources = await withCatalogSession('ensureGoodsThumb', (auth) =>
        getGoodsResources(auth, goods.id),
      )
      const previewResource =
        getResolvableFlatAssetThumbs(resources, resolveGoodsTopCategoryId(goods))[0]
        ?? resources.find((item) => !!item.resmd5 && !String(item.extra || '').includes('f_small'))
        ?? resources.find((item) => !!item.resmd5)
        ?? null
      if (!previewResource?.resmd5) return goods
      return {
        ...goods,
        thumbUrl: getMaterialPreviewUrl(previewResource.resmd5),
      }
    } catch {
      return goods
    }
  }

  async function selectGoods(goods: MaterialGoods | null) {
    previewRequestId += 1
    currentResources.value = []
    previewLoading.value = false
    currentGoods.value = goods
    if (!goods) return
    await loadPreview(goods)
  }

  async function syncCurrentGoodsWithVisibleList() {
    const currentId = Number(currentGoods.value?.id || 0)
    const nextGoods =
      goodsList.value.find((item) => Number(item.id || 0) === currentId)
      ?? goodsList.value[0]
      ?? null
    if (nextGoods?.id === currentGoods.value?.id) return
    await selectGoods(nextGoods)
  }

  async function setFavoritesOnly(enabled: boolean) {
    if (!enabled) {
      favoritesOnly.value = false
      const nextTopId = topCategories.value[0]?.id ?? selectedTopId.value
      if (nextTopId) {
        await activateTopCategory(nextTopId)
      }
      return
    }
    favoritesOnly.value = true
    resetSearchState(true)
    page.value = 1
    treeData.value = createFavoriteTree()
    expandedTreeKeys.value = []
    const first = treeData.value[0] ?? null
    if (!first) {
      selectedTreeKeys.value = []
      selectedTopId.value = ''
      selectedFilterId.value = ''
      resetCatalogResults()
      return
    }
    selectedTreeKeys.value = [first.key]
    selectedTopId.value = first.topId
    selectedFilterId.value = first.categoryId
    selectedFilterType.value = first.filterType ?? 0
    await reloadCurrentQuery()
  }

  async function ensureNodeChildren(node: MaterialTreeNode) {
    if (!isValidAuth(authRef.value) || node.nodeType !== 'l2' || node.loaded) return
    const pending = nodeLoadPromises.get(node.key)
    if (pending) {
      await pending
      return
    }
    const task = (async () => {
      const list = await withCatalogSession('ensureNodeChildren', (auth) =>
        getChildCategories(auth, node.categoryId, '3'),
      )
      const parentMeta = categoryMetaMap.value.get(node.categoryId)
      const parentIdentifyName = parentMeta?.identifyName ?? ''
      if (node.topId === Category.以爱之名 && list.length) {
        node.children = list.map((item) =>
          createLeafNode(
            node.topId,
            item.id,
            2,
            `${item.name}（${item.goods_count}）`,
            Number(item.goods_count || 0),
            undefined,
            String(item.identify_name || parentIdentifyName).trim(),
          ),
        )
      } else {
        node.children = [
          createLeafNode(node.topId, node.categoryId, 0, '全部', node.goodsCount, undefined, parentIdentifyName),
          ...list.map((item) =>
            createLeafNode(
              node.topId,
              item.id,
              2,
              `${item.name}（${item.goods_count}）`,
              Number(item.goods_count || 0),
              undefined,
              String(item.identify_name || parentIdentifyName).trim(),
            ),
          ),
        ]
      }
      node.loaded = true
      treeData.value = [...treeData.value]
    })()
    nodeLoadPromises.set(node.key, task)
    try {
      await task
    } finally {
      nodeLoadPromises.delete(node.key)
    }
  }

  async function applyLeafSelection(key: string, clearSearch: boolean) {
    const parsed = parseLeafKey(key)
    if (!parsed) return
    selectedTopId.value = parsed.topId
    selectedFilterId.value = parsed.filterId
    selectedFilterType.value = parsed.filterType
    selectedTreeKeys.value = [key]
    page.value = 1
    if (clearSearch) resetSearchState(true)
    await reloadCurrentQuery()
  }

  async function selectFirstLeafOf(node: MaterialTreeNode, requestId?: number) {
    expandTreeNode(node.key)
    await ensureNodeChildren(node)
    if (requestId !== undefined && requestId !== treeSelectRequestId) return
    expandTreeNode(node.key)
    const leaf = findFirstLeaf(node)
    if (leaf) {
      await applyLeafSelection(leaf.key, true)
    }
  }

  async function activateTopCategory(topId: string) {
    if (!isValidAuth(authRef.value)) return

    const requestId = ++topCategoryRequestId
    favoritesOnly.value = false
    selectedTopId.value = topId
    selectedFilterId.value = ''
    selectedFilterType.value = 0
    expandedTreeKeys.value = []
    selectedTreeKeys.value = []
    resetSearchState(true)
    page.value = 1
    resetCatalogResults()

    loadingList.value = true
    try {
      const level2List = await withCatalogSession('activateTopCategory', (auth) =>
        getChildCategories(auth, topId, '2'),
      )
      if (requestId !== topCategoryRequestId) return
      if (!level2List.length) {
        const topIdentifyName = topCategories.value.find((item) => item.id === topId)?.identify_name ?? ''
        const directLeaf = createLeafNode(topId, topId, 0, '全部', 0, undefined, topIdentifyName)
        treeData.value = [directLeaf]
        expandedTreeKeys.value = []
        await nextTick()
        if (requestId !== topCategoryRequestId) return
        await applyLeafSelection(directLeaf.key, true)
        return
      }
      treeData.value = level2List.map((item) => createL2Node(topId, item))
      await nextTick()
      if (requestId !== topCategoryRequestId) return
      const treeRequestId = ++treeSelectRequestId
      await selectFirstLeafOf(treeData.value[0]!, treeRequestId)
    } catch (error) {
      if (requestId !== topCategoryRequestId) return
      treeData.value = []
      resetCatalogResults()
      message.error(error instanceof Error ? error.message : '加载分类失败')
    } finally {
      if (requestId === topCategoryRequestId) {
        loadingList.value = false
      }
    }
  }

  async function reloadCurrentQuery() {
    const topCategory = currentTopCategory.value
    const requestId = ++listRequestId
    if (!isValidAuth(authRef.value)) return
    if (!favoritesOnly.value && !topCategory) return

    clearPreviewState()
    loadingList.value = true
    try {
      let result: MaterialGoodsQueryResult
      if (favoritesOnly.value) {
        const leafKey = String(selectedTreeKeys.value[0] ?? '')
        const leafNode = leafKey ? findNodeByKey(treeData.value, leafKey) as FavoriteTreeNode | null : null
        const goodsIds = leafNode?.favoriteGoodsIds ?? []
        const topId = String(leafNode?.topId || selectedTopId.value || '')
        const fullList = topId ? await loadFavoriteGoodsForTop(topId, goodsIds) : []
        treeData.value = createFavoriteTree()
        const start = (page.value - 1) * pageSize.value
        result = {
          list: fullList.slice(start, start + pageSize.value),
          total: fullList.length,
        }
      } else if (appliedSearch.value) {
        const params: MaterialSearchParams = {
          search: appliedSearch.value.text,
          search_type: appliedSearch.value.type,
          cid: topCategory!.id,
          belong_type: topCategory!.identify_name,
          page: page.value,
          limit: pageSize.value,
        }
        result = await withCatalogSession('searchGoods', (auth) => searchGoods(auth, params))
      } else {
        result = await withCatalogSession('getGoodsByCategory', (auth) =>
          getGoodsByCategory(auth, {
            cid: selectedFilterId.value,
            page: page.value,
            limit: pageSize.value,
            select_type: selectedFilterType.value,
          }),
        )

        if (!result.total) {
          const leafKey = String(selectedTreeKeys.value[0] ?? '')
          const leafNode = leafKey ? findNodeByKey(treeData.value, leafKey) : null
          if (leafNode?.nodeType === 'leaf') {
            result = { ...result, total: leafNode.goodsCount }
          }
        }
      }

      if (requestId !== listRequestId) return
      goodsList.value = await Promise.all(
        result.list.map((goods) => (goods.thumbUrl ? goods : ensureGoodsThumb(goods))),
      )
      total.value = result.total
      await syncCurrentGoodsWithVisibleList()
    } catch (error) {
      if (requestId !== listRequestId) return
      resetCatalogResults()
      message.error(error instanceof Error ? error.message : '加载素材列表失败')
    } finally {
      if (requestId === listRequestId) {
        loadingList.value = false
      }
    }
  }

  async function runSearch() {
    const text = searchText.value.trim()
    if (!text) {
      resetSearchState(false)
      page.value = 1
      await reloadCurrentQuery()
      return
    }
    appliedSearch.value = {
      text,
      type: searchType.value,
    }
    page.value = 1
    await reloadCurrentQuery()
  }

  async function clearSearch() {
    if (!searchText.value && !appliedSearch.value) return
    searchText.value = ''
    resetSearchState(false)
    page.value = 1
    await reloadCurrentQuery()
  }

  async function changePage(nextPage: number) {
    page.value = nextPage
    await reloadCurrentQuery()
  }

  async function updatePageSize(nextPageSize: number) {
    const normalized = Math.max(1, Math.floor(nextPageSize))
    if (pageSize.value === normalized) return
    pageSize.value = normalized
    page.value = 1
    if (!loadingInit.value && (selectedFilterId.value || appliedSearch.value || favoritesOnly.value)) {
      await reloadCurrentQuery()
    }
  }

  async function handleTreeLoad(option: TreeOption) {
    const node = option as MaterialTreeNode
    await ensureNodeChildren(node)
    expandTreeNode(node.key)
  }

  async function handleTreeSelect(keys: Array<string | number>) {
    const key = String(keys[0] ?? '')
    if (!key) return
    const requestId = ++treeSelectRequestId
    const node = findNodeByKey(treeData.value, key)
    if (!node) return
    if (node.nodeType === 'leaf') {
      if (requestId !== treeSelectRequestId) return
      await applyLeafSelection(node.key, true)
      return
    }
    await selectFirstLeafOf(node, requestId)
  }

  async function initialize(force = false) {
    const currentAuth = authRef.value
    if (!isValidAuth(currentAuth)) return
    if (!force && topCategories.value.length) return

    loadingInit.value = true
    try {
      const topList = filterVisibleTopCategories(await withCatalogSession('initialize', (auth) => getTopCategories(auth)))
      registerKnownPortraitCategories(topList)
      topCategories.value = topList
      categoryMetaMap.value = new Map()
      for (const top of topList) {
        registerCategoryMeta(top.id, top.id, top.identify_name)
      }
      await refreshCollection()
      treeData.value = []
      expandedTreeKeys.value = []
      selectedTreeKeys.value = []
      selectedTopId.value = ''
      selectedFilterId.value = ''
      resetCatalogResults()
      page.value = 1
      resetSearchState(true)
      if (topList.length) {
        await nextTick()
        await activateTopCategory(topList[0]!.id)
      } else {
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : '素材库初始化失败')
    } finally {
      loadingInit.value = false
    }
  }

  async function toggleCurrentCollection() {
    const goods = currentGoods.value
    if (!isValidAuth(authRef.value) || !goods || !selectedTopId.value) return
    try {
      await withCatalogSession('toggleCurrentCollection', (auth) =>
        toggleCollection(auth, goods.id, selectedTopId.value),
      )
      const goodsId = Number(goods.id || 0)
      if (goodsId > 0) {
        favoriteGoodsCache.set(goodsId, goods)
      }
      await refreshCollection()
      invalidateFavoriteGoodsCache(selectedTopId.value)
      await syncCurrentGoodsWithVisibleList()
    } catch (error) {
      message.error(error instanceof Error ? error.message : '收藏操作失败')
    }
  }

  return {
    pageSize,
    loadingInit,
    loadingList,
    previewLoading,
    topCategories,
    treeData: treeData as Ref<MaterialTreeNode[]>,
    expandedTreeKeys,
    selectedTreeKeys,
    goodsList,
    currentGoods,
    currentResources,
    collectionGoodsIds,
    favoritesOnly,
    selectedTopId,
    page,
    total,
    searchText,
    searchType,
    topCategoryMode,
    isRecommendTopCategory,
    currentGoodsTopCategoryId,
    resolveGoodsTopCategoryId,
    currentCollected,
    hasSearch,
    initialize,
    activateTopCategory,
    handleTreeLoad,
    handleTreeSelect,
    runSearch,
    clearSearch,
    changePage,
    updatePageSize,
    selectGoods,
    setFavoritesOnly,
    toggleCurrentCollection,
  }
}
